import { useEffect, useState } from "react"
import { PositionData, BillGet, adressType } from "../data/Types"
import dayjs from "dayjs"
import isSameOrBefore from "dayjs/plugin/isSameOrBefore"
import { CoordinateType, sessionCalendarDataType, sessionCalendarTransferDataType, sessionGetDataType, TaskActionCode } from "../pages/Aufgaben/types"
import { ObjectType } from "../pages/Objekte/types/types"
import { monthDataArray } from "../pages/Buchhaltung/components/addBillModal/AddBillTemplateModalScreen1"
import { AbsenceStatus, AbsenceType } from "../pages/Abwesenheiten/types"
import { AbsencesTypes, LocalStorageKeys } from "../api/constants"
import Holidays from "date-holidays"
import { TReportMaterial } from "../pages/Berichte/types"

dayjs.extend(isSameOrBefore)

// prevent the tabulator key down event
export const preventKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement> | React.KeyboardEvent<HTMLTextAreaElement> | React.KeyboardEvent<HTMLSelectElement>
) => {
    if (event.key === "Tab") {
        event.preventDefault()
    }
}

// compare values - return true if a value is different to its comparison
export const compareValues = (
    values: {
        v1: string | number | boolean | undefined
        v2: string | number | boolean | undefined
    }[]
) => {
    const bool = values.map((item) => {
        if (item.v1 !== item.v2) {
            return false
        } else {
            return true
        }
    })
    const result = bool.includes(false)
    return result
}

// extract suffix from file name
export function getSuffix(fileName: string) {
    const dotIndex = fileName.lastIndexOf(".")

    if (dotIndex === -1 || dotIndex === fileName.length - 1) {
        return ""
    }

    return fileName.substring(dotIndex + 1)
}

// check if value is empty - returns TRUE if there are empty values given
export const checkForEmptyValues = (values: (string | number | Number)[]) => {
    const bool: any = values.map((item) => {
        if (item === "" || item === undefined || item === 0) {
            return true
        } else {
            return false
        }
    })

    const result = bool.includes(true)
    return result
}

// calculates total amount from bill
export const calculateTotal = (bill: BillGet) => {
    return bill?.positions
        ?.map((position) => position.amount * position.singlePrice * (1 + 0.01 * position.tax))
        .reduce((acc, value) => acc + value, 0)
        .toFixed(2)
        .replace(".", ",")
}

// calculates total amount from positions
export const calculatePositionTotal = (positions: PositionData[]) => {
    return positions
        ?.map((position) => position.amount * position.singlePrice * 1.19)
        .reduce((acc, value) => acc + value, 0)
        .toFixed(2)
        .replace(".", ",")
}

// validate email input
export const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
}

// humanizes mail type in FE
export const returnHumanizedMailtype = (mailType: string) => {
    switch (mailType) {
        case "bill":
            return "Rechnung"
        case "offer":
            return "Angebot"
        case "reminder":
            return "Mahnung"
        case "cancel_bill":
            return "Stornierung"
        default:
            return
    }
}

// delays pagination request for a few seconds
export const useDelayWhenSearchWithPagination = (value: string, storageKey?: string) => {
    const [debouncedValue, setdebouncedValue] = useState(value)

    useEffect(() => {
        const handler = setTimeout(() => {
            setdebouncedValue(value)
            if (storageKey) {
                sessionStorage.setItem(storageKey, JSON.stringify(value))
            }
        }, 1000)

        return () => {
            clearTimeout(handler)
        }
    }, [value])

    return debouncedValue ?? ""
}

// resets the page of a paginated list to 1 as soon as the (debounced) search term or a filter changes
// runs during render, so the query never starts with the new filter and the old page
export const useResetPageOnFilterChange = (filters: unknown[], resetPage: () => void) => {
    const serializedFilters = JSON.stringify(filters)
    const [previousFilters, setpreviousFilters] = useState(serializedFilters)
    if (previousFilters !== serializedFilters) {
        setpreviousFilters(serializedFilters)
        resetPage()
    }
}

// formats the currency
export function formatCurrency(amount: number, currency = "EUR", locale = "de-DE") {
    if (isNaN(amount)) return amount
    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currency,
    }).format(amount)
}

// checks if a date is between today and the today in x days
export function checkDate(date: Date, timeFrame: number) {
    const now = dayjs()
    const futureDate = now.add(timeFrame, "day")
    const givenDate = dayjs(date)
    return givenDate.isAfter(now.subtract(1, "day")) && givenDate.isBefore(futureDate.add(1, "day"))
}

// export join address from object-object
export function returnAddressString(obj: ObjectType | adressType) {
    if (obj && typeof obj !== "string" && "adress" in obj) {
        return obj?.adress?.street + " " + obj?.adress?.nr + ", " + obj?.adress?.plz + " " + obj?.adress?.city
    } else {
        return obj?.street + " " + obj?.nr + ", " + obj?.plz + " " + obj?.city
    }
}

// transform session-data from db-type into calendar-type
export function rearrangeSessionDataForCalendar(sessionData: sessionGetDataType[], user_id: string) {
    const rearrangedSessions = sessionData?.map((item: sessionGetDataType) => ({
        title: item.title,
        id: item._id,
        start: item.startAt,
        end: item.endAt,
        calendarId: item.routeOptimization?.optimizedAt ? "cal1.1" : "cal1",
        category: "time",
        user: user_id,
        object_id: typeof item.object !== "string" && item.object._id,
        location:
            typeof item.object !== "string" &&
            item.object.adress.street + " " + item.object.adress.nr + ", " + item.object.adress.plz + " " + item.object.adress.city,
        totalDurationMinutesEstimated: item.totalDurationMinutesEstimated,
        totalDurationMinutesActual: item.totalDurationMinutesActual,
        checkInStatus: item.checkInStatus,
        status: item.status,
        assignedBy: item.assignedBy,
        description: item.description,
        tasks: item.tasks,
        attendees: item.attendees,
        routeOptimization: item?.routeOptimization,
    })) as unknown as sessionCalendarDataType[]
    const transfersFromOptimizedSessions = sessionData?.map(
        (item: sessionGetDataType) =>
            item.routeOptimization &&
            item.routeOptimization.transferFromLastSession.timeInSeconds > 0 && {
                transferDataset: true,
                title: "Fahrtweg " + (item.routeOptimization.transferFromLastSession.distanceInMeters / 1000).toFixed(2) + " km",
                id: item._id + "Fahrtweg",
                start: new Date(item.startAt).getTime() - item.routeOptimization.transferFromLastSession.timeInSeconds * 1000,
                end: item.startAt,
                calendarId: "cal3",
                category: "time",
                user: user_id,
                totalDurationMinutesEstimated: item.routeOptimization.transferFromLastSession.timeInSeconds / 60,
                attendees: item.attendees,
            }
    ) as unknown as sessionCalendarTransferDataType[]
    return [...rearrangedSessions, ...(transfersFromOptimizedSessions ?? [])] as (sessionCalendarDataType | sessionCalendarTransferDataType)[]
}

export function rearrangeAbsencesDataForCalendar(absencesData: AbsenceType[], employeeName: string) {
    return absencesData
        ?.filter((item) => item.status !== AbsenceStatus.REJECTED)
        .map((item) => ({
            title: `${item.type} ${item.type === AbsencesTypes.SICK ? "" : `(${item.status})`} - ${employeeName}`,
            id: item._id,
            category: "allday",
            isAllDay: true,
            start: item.startDate,
            end: item.endDate,
            calendarId: "cal2",
        })) as unknown as sessionCalendarDataType[]
}
// transform session-data from calendar-type into db-type for post
export function rearrangeSessionDataForDB(sessionData: sessionCalendarDataType, obj: ObjectType) {
    return {
        title: returnAddressString(obj),
        description: sessionData?.description,
        attendees: sessionData?.attendees?.map((item) => item._id),
        tasks: sessionData?.tasks?.map((item) => item._id),
        object: sessionData?.object_id,
        status: "scheduled",
        startAt: dayjs(`${sessionData?.date}T${sessionData.start}:00`).toDate(),
        endAt: dayjs(`${sessionData.date}T${sessionData.end}:00`).toDate(),
    } as sessionGetDataType
}

// transform session-data from calendar-type into db-type for update
export function rearrangeSessionDataForDBUpdate(sessionData: sessionCalendarDataType, actualSession: sessionCalendarDataType) {
    const { startAt, endAt } = (() => {
        return {
            startAt: dayjs(
                `${sessionData.date ?? dayjs(actualSession.start).format("YYYY-MM-DD")}T${
                    sessionData.start ?? dayjs(actualSession.start).format("HH:mm:ss")
                }`
            ).toDate(),
            endAt: dayjs(
                `${sessionData.date ?? dayjs(actualSession.end).format("YYYY-MM-DD")}T${
                    sessionData.end ?? dayjs(actualSession.end).format("HH:mm:ss")
                }`
            ).toDate(),
        }
    })()
    return {
        description: sessionData?.description ?? actualSession.description,
        startAt: startAt,
        endAt: endAt,
    } as sessionGetDataType
}

// transform the TaskActionCode in Human Readable Format
export function humanizeTaskActionCode(code: (typeof TaskActionCode)[keyof typeof TaskActionCode]["value"]) {
    const entries = Object.entries(TaskActionCode) as [keyof typeof TaskActionCode, (typeof TaskActionCode)[keyof typeof TaskActionCode]][]
    for (const [, entry] of entries) {
        if (entry.value === code) {
            return entry.translation
        }
    }
}

// get Location from device in that very moment
export async function getLocationFromDevice(): Promise<CoordinateType> {
    if ("geolocation" in navigator) {
        // Helper function to get location with specific accuracy
        const getGeolocation = (enableHighAccuracy: boolean): Promise<CoordinateType> => {
            return new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        const { latitude, longitude, accuracy } = position.coords
                        resolve({
                            latitude,
                            longitude,
                            accuracy,
                        })
                    },
                    (error) => {
                        console.error("Error getting location:", error.message)
                        reject(error.message) // Reject with the error
                    },
                    {
                        enableHighAccuracy: enableHighAccuracy,
                        timeout: 5000, // Maximum wait time in milliseconds
                        maximumAge: 0, // Do not use cached location
                    }
                )
            })
        }

        try {
            // Get high and low accuracy results concurrently
            const [highAccuracyResult, lowAccuracyResult] = await Promise.allSettled([getGeolocation(true), getGeolocation(false)])

            // Extract successful results
            const highAccuracyLocation = highAccuracyResult.status === "fulfilled" ? highAccuracyResult.value : null
            const lowAccuracyLocation = lowAccuracyResult.status === "fulfilled" ? lowAccuracyResult.value : null

            // Determine the better result based on accuracy
            if (highAccuracyLocation && lowAccuracyLocation) {
                return highAccuracyLocation.accuracy <= lowAccuracyLocation.accuracy ? highAccuracyLocation : lowAccuracyLocation
            }

            // Fallback to whichever result succeeded
            return highAccuracyLocation || lowAccuracyLocation || Promise.reject("No location found")
        } catch (error) {
            console.error("Error during geolocation:", error)
            throw error
        }
    } else {
        throw new Error("Geolocation is not supported by this browser.")
    }
}

// shuts down camera after use
export function shutDownCamera() {
    navigator.mediaDevices
        .getUserMedia({ video: true })
        .then((stream) => {
            const tracks = stream.getTracks() // Get all media tracks
            tracks.forEach((track) => track.stop()) // Stop each track
        })
        .catch((error) => {
            console.error("Error stopping the camera:", error)
        })
}

export const validateTimeToHalfHour = (_: any, value: string) => {
    if (!value) {
        return Promise.resolve()
    }
    
    const [hours, minutes] = value.split(":")
    const minutesNum = parseInt(minutes)
    
    if (minutesNum === 0 || minutesNum === 30 || minutesNum === 45 || minutesNum === 15) {
        return Promise.resolve()
    }
    
    return Promise.reject(new Error("Zeit muss eine volle oder halbe Stunde sein (00 oder 30 Minuten)"))
}

// transform error message in human readable format
export const erroeMessageHumanzing = (error: Error) => {
    console.log(error)
}

// general translater of states/actioncodes
export const translateStateOrActionCode = (cryptic: string, translaterArray: { value: string; translation: string }[]) => {
    return translaterArray.find((item) => item.value === cryptic)?.translation
}

// Breaks Thousands with point
export const returnThousanderBreakInNumber = (num: number) => {
    return Number(num?.toFixed(2)).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 20 }) || "0"
}

// takes array of bills and returns amount of totalNetto
export const returnTotalNettoAmount = (bills: BillGet[]) => {
    let amount = 0
    bills?.forEach((item) => {
        amount += item.totalNetto
    })
    return amount
}

// takes array of numbers and returns string of months in german
export const returnMonthNames = (months: number[]) => {
    return monthDataArray
        .filter((item) => months.includes(item.value))
        .map((item) => item.key)
        .map((item, index) => item + (index !== months.length - 1 ? ", " : ""))
}

// minutes to hours and minutes
export const minutesToHoursAndMinutes = (minutes: number) => {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return `${hours}h ${mins.toFixed(0)}min`
}

export const processAbsencesOfEmployee = (absences: AbsenceType[]) => {
    const sickAbsences = absences.filter((absence) => absence.type === AbsencesTypes.SICK)
    const vacationAbsencesApproved = absences.filter(
        (absence) => absence.type === AbsencesTypes.VACATION && absence.status === AbsenceStatus.APPROVED
    )
    const vacationAbsencesInReview = absences.filter(
        (absence) => absence.type === AbsencesTypes.VACATION && absence.status === AbsenceStatus.IN_REVIEW
    )
    const vacationAbsencesRejected = absences.filter(
        (absence) => absence.type === AbsencesTypes.VACATION && absence.status === AbsenceStatus.REJECTED
    )
    return {
        sickAbsences,
        vacationAbsencesApproved,
        vacationAbsencesInReview,
        vacationAbsencesRejected,
    }
}

// Generate a unique set of years from the absences
const getYearsInAbsences = (absences: AbsenceType[]): number[] => {
    const years = new Set<number>()
    absences.forEach(({ startDate, endDate }) => {
        const startYear = dayjs(startDate).year()
        const endYear = dayjs(endDate).year()
        for (let y = startYear; y <= endYear; y++) {
            years.add(y)
        }
    })
    return Array.from(years)
}

export const getDayCountOfAbsences = (absences: AbsenceType[]): number => {
    if (!absences.length) return 0

    const hd = new Holidays("DE", "SN") // Set your region here
    const years = getYearsInAbsences(absences)

    // Collect all holidays across relevant years
    const holidayDates = years.flatMap((year) => hd.getHolidays(year).map((h) => dayjs(h.date).format("YYYY-MM-DD")))

    const isBusinessDay = (date: dayjs.Dayjs): boolean => {
        const day = date.day()
        const formatted = date.format("YYYY-MM-DD")
        return day !== 0 && day !== 6 && !holidayDates.includes(formatted)
    }

    return absences.reduce((acc, absence) => {
        let current = dayjs(absence.startDate)
        const end = dayjs(absence.endDate)
        let count = 0

        while (current.isSameOrBefore(end, "day")) {
            if (isBusinessDay(current)) count++
            current = current.add(1, "day")
        }

        return acc + count
    }, 0)
}

// get data from session storage
export const getDataFromSessionStorage = (key: string) => {
    const data = sessionStorage.getItem(key)
    if (data && data !== "undefined") return JSON.parse(data)
    return null
}

// clear session storage
export const clearSessionStorageByPrefix = () => {
    Object.values(LocalStorageKeys).forEach((value) => {
        Object.keys(sessionStorage).forEach((key) => {
            if (key.startsWith(value)) {
                sessionStorage.removeItem(key)
            }
        })
    })
}

// transform minutes to seconds
export const transformMinutesToSeconds = (minutes: number) => {
    return minutes * 60
}

// transform seconds to minutes
export const transformSecondsToMinutes = (seconds: number) => {
    return seconds / 60
}

// load from local storage with expiration
export const loadWithExpiryFromLocalStorage = (key: string) => {
    const itemStr = localStorage.getItem(key)
    if (!itemStr) return null

    const item = JSON.parse(itemStr)
    const now = new Date()

    if (now.getTime() > item.expiry) {
        // Item expired
        localStorage.removeItem(key)
        return null
    }

    return item.value
}

export const findElementsWithExactCharCount = (arr: string[], treeRoot: string): string[] => {
    if (treeRoot === "") {
        // get root folders -> folder with only two "/"
    return arr?.filter(str => {
      let count = 0;
      for (let i = 0; i < str.length; i++) {
        if (str[i] === "/") {
          count++;
        }
      }
      return count === 2;
    });
  } else {
    // find all folders of the selected root folder
    const selectedRootFolders = arr?.filter(item => item.startsWith(treeRoot) && item.length !== treeRoot.length)
    return selectedRootFolders
  }
}


// convert hex-code in hsl-code
export const hexToHsl = (hex: string) => {
    let r = 0,
        g = 0,
        b = 0
    if (hex.length === 7) {
        // #RRGGBB
        r = parseInt(hex.substring(1, 3), 16)
        g = parseInt(hex.substring(3, 5), 16)
        b = parseInt(hex.substring(5, 7), 16)
    } else if (hex.length === 4) {
        // #RGB
        r = parseInt(hex[1] + hex[1], 16)
        g = parseInt(hex[2] + hex[2], 16)
        b = parseInt(hex[3] + hex[3], 16)
    }
    r /= 255
    g /= 255
    b /= 255
    let max = Math.max(r, g, b),
        min = Math.min(r, g, b)
    let h,
        s,
        l = (max + min) / 2
    if (max === min) {
        h = 0
        s = 0
        l = Math.round(l * 100)
    } else {
        let d = max - min
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0)
                break
            case g:
                h = (b - r) / d + 2
                break
            case b:
                h = (r - g) / d + 4
                break
        }
        h = Math.round((h ?? 0) * 60)
        s = Math.round(s * 100)
        l = Math.round(l * 100)
    }
    return `${h} ${s}% ${l}%` // Tailwind expects HSL without "hsl()"
}


export const transformReportMaterialsAndInformationToBillPositions = (materials: TReportMaterial[], information: string): PositionData[] => {
    return [
        {
            description: information,
            amount: 1,
            singlePrice: 0,
            position_id: "1",
            tax: 19,
            unit: "Festpreis"
        },
        ...materials.map((material, index) => ({
            description: material.materialName,
            amount: 1,
            singlePrice: material.totalPrice,
            position_id: (index + 2).toString(),
            tax: 7,
            unit: "Festpreis"
        })),
    ]
}